import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMoneyBillWave,
  faChevronDown,
  faChevronRight,
  faBoxArchive,
} from "@fortawesome/free-solid-svg-icons";
import PaymentCard from "../components/PaymentCard";
import AddPaymentModal from "../components/AddPaymentModal";
import PaymentDetailModal from "../components/PaymentDetailModal";

function MonthlyPayments() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [rates, setRates] = useState(null);

  // Separate active and archived payments
  const activePayments = payments.filter((p) => !p.archived);
  const archivedPayments = payments.filter((p) => p.archived);

  // Calculate totals in EUR
  const convertToEur = (amount, currency) => {
    if (!rates || !amount) return Number(amount) || 0;
    if (currency === "EUR") return Number(amount);
    const rate = rates[currency];
    if (!rate) return Number(amount);
    return Number(amount) / rate;
  };

  const totalMonthlyEur = activePayments.reduce((sum, p) => {
    return sum + convertToEur(p.amount, p.currency);
  }, 0);

  const unpaidMonthlyEur = activePayments
    .filter((p) => !p.paid)
    .reduce((sum, p) => {
      return sum + convertToEur(p.amount, p.currency);
    }, 0);

  useEffect(() => {
    fetchPayments();
    fetchRates();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/monthly-payments`
      );
      setPayments(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRates = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/exchange-rates`
      );
      if (response.data?.rates) {
        setRates(response.data.rates);
      }
    } catch (err) {
      console.error("Error fetching exchange rates:", err);
    }
  };

  const handleTogglePaid = async (id, currentPaidStatus) => {
    // Optimistic update
    setPayments((prev) =>
      prev.map((p) => (p.id === id ? { ...p, paid: !currentPaidStatus } : p))
    );

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/monthly-payments/${id}/paid`,
        {
          paid: !currentPaidStatus,
        }
      );
    } catch (err) {
      setError(err.message);
      fetchPayments();
    }
  };

  const handlePaymentAdded = () => {
    fetchPayments();
  };

  const handlePaymentUpdate = (updatedPayment) => {
    setPayments((prev) =>
      prev.map((p) => (p.id === updatedPayment.id ? updatedPayment : p))
    );
    setSelectedPayment(updatedPayment);
  };

  const handlePaymentDelete = (id) => {
    setPayments((prev) => prev.filter((p) => p.id !== id));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50"
        role="alert"
      >
        <span className="font-medium">{t("error")}:</span> {error}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl lg:max-w-5xl mx-auto space-y-6"
    >
      {/* Add Button - positioned to align with page header */}
      <div className="flex justify-end -mt-16">
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary p-3 rounded-xl shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {/* Summary Card */}
      {activePayments.length > 0 && rates && (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-primary-600 dark:via-primary-700 dark:to-primary-800 rounded-2xl p-5 text-white shadow-lg shadow-primary-500/25 dark:shadow-primary-900/30"
        >
          {/* Subtle decorative circles */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full" />
          <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-white/5 rounded-full" />

          {/* Content */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-white/70 text-sm font-medium mb-1">
                  {t("totalMonthly", "Загалом на місяць")}
                </p>
                <p className="text-2xl font-bold">
                  {totalMonthlyEur.toFixed(0)} €
                </p>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-sm font-medium mb-1">
                  {t("unpaidThisMonth", "Не сплачено")}
                </p>
                <p className="text-2xl font-bold">
                  {unpaidMonthlyEur.toFixed(0)} €
                </p>
              </div>
            </div>

            {/* Progress section */}
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
              <div className="flex justify-between items-center text-sm mb-2">
                <span className="text-white/80">
                  {activePayments.filter((p) => p.paid).length} /{" "}
                  {activePayments.length} {t("paidThisMonth", "сплачено")}
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{
                    width: `${
                      activePayments.length
                        ? (activePayments.filter((p) => p.paid).length /
                            activePayments.length) *
                          100
                        : 0
                    }%`,
                  }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="bg-white h-2 rounded-full"
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Active Payments Grid */}
      {activePayments.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FontAwesomeIcon
            icon={faMoneyBillWave}
            className="text-4xl mb-3 text-gray-300 dark:text-gray-600"
          />
          <p>
            {t("noMonthlyPaymentsFound", "Щомісячних платежів не знайдено")}
          </p>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="mt-4 text-primary-600 dark:text-primary-400 font-medium hover:underline"
          >
            {t("addFirstPayment", "Додати перший платіж")}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {activePayments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                onTogglePaid={handleTogglePaid}
                onClick={setSelectedPayment}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Archived Section */}
      {archivedPayments.length > 0 && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setIsArchivedOpen(!isArchivedOpen)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium w-full py-2 transition-colors"
          >
            <FontAwesomeIcon
              icon={isArchivedOpen ? faChevronDown : faChevronRight}
              size="sm"
            />
            <FontAwesomeIcon icon={faBoxArchive} className="text-gray-400" />
            {t("archivedPayments", "Архів")} ({archivedPayments.length})
          </button>

          <AnimatePresence>
            {isArchivedOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                  {archivedPayments.map((payment) => (
                    <PaymentCard
                      key={payment.id}
                      payment={payment}
                      onTogglePaid={handleTogglePaid}
                      onClick={setSelectedPayment}
                      isArchived={true}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Add Payment Modal */}
      <AnimatePresence>
        {isAddModalOpen && (
          <AddPaymentModal
            onClose={() => setIsAddModalOpen(false)}
            onPaymentAdded={handlePaymentAdded}
          />
        )}
      </AnimatePresence>

      {/* Payment Detail Modal */}
      <AnimatePresence>
        {selectedPayment && (
          <PaymentDetailModal
            payment={selectedPayment}
            onClose={() => setSelectedPayment(null)}
            onUpdate={handlePaymentUpdate}
            onDelete={handlePaymentDelete}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default MonthlyPayments;
